import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  HStack,
  Heading,
  IconButton,
  Input,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import axios from "axios";
import React, { useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { CiImageOn } from "react-icons/ci";
import { FaTimes } from "react-icons/fa";
import { toast } from "react-toastify";
import { fetchController } from "../../../utils/FetchController/fetchController";
import Loader from "../../../utils/Loader/Loader";
import { getHostDomain } from "../../../utils/utils";
import AgencyScript from "../AgencyScript";
import Smtp from "../Smtp";
import Social from "../Social";
import SupportLinks from "../SupportLinks";
import { AgencyContext } from "../../Settings/AgencyProvider/AgencyProvider";

// SectionShell: consistent panel wrapper for every tab.
function SectionShell({ title, description, children, action }) {
  return (
    <Card variant="outline">
      <CardBody>
        <Flex justify="space-between" align="flex-start" mb={4} gap={4}>
          <Box>
            <Heading size="sm" fontWeight="semibold">
              {title}
            </Heading>
            {description && (
              <Text fontSize="sm" color="gray.500" mt={1}>
                {description}
              </Text>
            )}
          </Box>
          {action}
        </Flex>
        {children}
      </CardBody>
    </Card>
  );
}

// IdentityPanel: agency name + agency contact email.
function IdentityPanel({ agency, isValidate, canWrite }) {
  const {
    buttonColorScheme,
    handleCreateAgency,
    agencyData,
    agencyName,
    setAgencyName,
  } = agency;
  return (
    <SectionShell
      title="Identity"
      description="Used in page titles, customer-facing emails, and the subaccount portal in place of Insighto.ai."
      action={
        <Button
          colorScheme={buttonColorScheme}
          onClick={handleCreateAgency}
          isDisabled={!canWrite}
          size="sm"
        >
          {!agencyData ? "Create" : "Save"}
        </Button>
      }
    >
      <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={4} maxW="720px">
        <FormControl isRequired isInvalid={isValidate && !agencyName}>
          <FormLabel fontSize="sm">Agency name</FormLabel>
          <Input
            value={agencyName || ""}
            onChange={(e) => setAgencyName(e.target.value)}
            placeholder="Acme AI"
          />
          {!agencyName && (
            <FormErrorMessage>Agency name is required</FormErrorMessage>
          )}
        </FormControl>
      </Grid>
    </SectionShell>
  );
}

// LogoColorPanel: logo upload + color picker with preset chips.
function LogoColorPanel({
  agency,
  logo,
  handleLogoChange,
  handleLogoRemove,
  canWrite,
}) {
  const {
    buttonColorScheme,
    handleBrandCreate,
    agencyData,
    themeColor,
    isPending,
    predefinedColors,
    setThemeColor,
  } = agency;
  return (
    <SectionShell
      title="Logo & color"
      description="Your logo and brand color propagate to the subaccount portal and outbound emails."
      action={
        <Button
          colorScheme={buttonColorScheme}
          isDisabled={!agencyData || !canWrite}
          isLoading={isPending}
          onClick={handleBrandCreate}
          size="sm"
        >
          Save
        </Button>
      }
    >
      <Flex gap={8} direction={{ base: "column", md: "row" }} align="flex-start">
        {/* Logo */}
        <Box minW="140px">
          <Text
            fontSize="xs"
            textTransform="uppercase"
            color="gray.500"
            fontWeight="bold"
            letterSpacing="0.5px"
            mb={2}
          >
            Logo
          </Text>
          <Flex align="center">
            {logo ? (
              <Box position="relative" w="120px" h="120px">
                <label htmlFor="file-upload">
                  <img
                    src={logo}
                    alt="Logo"
                    style={{
                      width: "120px",
                      height: "120px",
                      cursor: "pointer",
                      objectFit: "contain",
                      border: "1px solid #E2E8F0",
                      borderRadius: "8px",
                    }}
                  />
                </label>
                <IconButton
                  icon={<FaTimes />}
                  size="xs"
                  position="absolute"
                  top="-8px"
                  right="-8px"
                  colorScheme={buttonColorScheme}
                  onClick={handleLogoRemove}
                  aria-label="Remove Logo"
                  isDisabled={!canWrite}
                />
              </Box>
            ) : (
              <Box
                w="120px"
                h="120px"
                borderWidth={1}
                borderStyle="dashed"
                borderRadius="md"
                display="flex"
                alignItems="center"
                justifyContent="center"
                color="gray.400"
                fontSize="36px"
                cursor="pointer"
              >
                <label
                  htmlFor="file-upload"
                  style={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                    height: "100%",
                  }}
                >
                  <CiImageOn />
                </label>
              </Box>
            )}
            <Input
              id="file-upload"
              type="file"
              accept="image/*"
              onChange={handleLogoChange}
              display="none"
              isDisabled={!agencyData || !canWrite}
            />
          </Flex>
          <Text fontSize="xs" color="gray.400" mt={2}>
            PNG or SVG, ideally square.
          </Text>
        </Box>

        {/* Color picker + presets */}
        <Box flex={1} minW={0}>
          <Text
            fontSize="xs"
            textTransform="uppercase"
            color="gray.500"
            fontWeight="bold"
            letterSpacing="0.5px"
            mb={2}
          >
            Brand color
          </Text>
          <HStack spacing={3} mb={3} align="center">
            {/* Native color picker — large, clickable */}
            <Box
              w="44px"
              h="44px"
              borderRadius="md"
              borderWidth="1px"
              borderColor="gray.300"
              overflow="hidden"
              position="relative"
              flexShrink={0}
            >
              <Input
                type="color"
                value={themeColor || "#3182CE"}
                onChange={(e) => setThemeColor(e.target.value)}
                p={0}
                border="none"
                w="100%"
                h="100%"
                cursor="pointer"
                sx={{
                  "&::-webkit-color-swatch-wrapper": { padding: 0 },
                  "&::-webkit-color-swatch": { border: "none" },
                }}
              />
            </Box>
            <Box>
              <Text fontSize="sm" fontWeight="medium" textTransform="uppercase">
                {themeColor || "—"}
              </Text>
              <Text fontSize="xs" color="gray.500">
                Pick any color or choose a preset →
              </Text>
            </Box>
          </HStack>
          <Text
            fontSize="xs"
            textTransform="uppercase"
            color="gray.500"
            fontWeight="bold"
            letterSpacing="0.5px"
            mb={2}
          >
            Presets
          </Text>
          <Flex wrap="wrap" gap={2}>
            {predefinedColors?.map((color, index) => {
              const selected = themeColor === color.color;
              return (
                <Tooltip key={color.name + index} label={color.name}>
                  <Box
                    w="28px"
                    h="28px"
                    bg={color.color}
                    cursor="pointer"
                    onClick={() => setThemeColor(color.color)}
                    borderRadius="md"
                    outline={selected ? "2px solid" : "none"}
                    outlineColor={selected ? color.color : "transparent"}
                    outlineOffset="2px"
                    _hover={{ opacity: 0.85 }}
                    transition="all 0.15s"
                  />
                </Tooltip>
              );
            })}
          </Flex>
        </Box>
      </Flex>
    </SectionShell>
  );
}

const Branding = () => {
  const baseUrl = getHostDomain();
  const agency = useContext(AgencyContext);
  const [logo, setLogo] = useState(null);
  const {
    buttonColorScheme,
    agencyData,
    isValidate,
    isLoading,
    textColor,
    getAgencies,
    staffRole,
  } = agency;
  const canWrite = !staffRole || staffRole === "admin";

  const handleLogoChange = async (event) => {
    const file = event.target.files[0];
    const url = baseUrl + `/api/v1/agency/${agencyData?.id}/upload_logo`;
    const accessToken = localStorage.getItem("token");
    if (file) {
      setLogo(URL.createObjectURL(file));
      let data = new FormData();
      data.append("upload_agency_logo", file);
      let config = {
        method: "post",
        maxBodyLength: Infinity,
        url: url,
        headers: {
          Accept: "*/*",
          Authorization: `Bearer ${accessToken}`,
        },
        data: data,
      };

      axios
        .request(config)
        .then(() => {
          toast.success("Logo uploaded successfully.");
          getAgencies();
        })
        .catch((error) => {
          toast.error("Something went wrong.");
          console.error(error);
        });
    }
  };

  const handleLogoRemove = async () => {
    setLogo(null);
    try {
      await fetchController(
        baseUrl + `/api/v1/agency/${agencyData.id}`,
        "PUT",
        {
          branding: {
            ...agency?.agencyData?.branding,
            logo_url: null,
          },
        }
      );
      toast.success("Logo deleted");
      getAgencies();
    } catch (error) {
      console.error(error);
      toast.error(error.message || "Something went wrong");
    }
  };

  const defaultSmtp = {
    host: agency?.agencyData?.branding?.smtp?.host || "",
    port: agency?.agencyData?.branding?.smtp?.port || "",
    username: agency?.agencyData?.branding?.smtp?.username || "",
    password: agency?.agencyData?.branding?.smtp?.password || "",
    email: agency?.agencyData?.branding?.smtp?.email || "",
  };
  // Prefer the canonical `socials` (plural) but fall back to legacy `social`
  // so older agencies don't see empty fields on first load.
  const sourceSocials =
    agency?.agencyData?.branding?.socials ||
    agency?.agencyData?.branding?.social ||
    {};
  const defaultSocial = {
    twitter: sourceSocials.twitter || "",
    facebook: sourceSocials.facebook || "",
    linkedin: sourceSocials.linkedin || "",
    instagram: sourceSocials.instagram || "",
    discord: sourceSocials.discord || "",
    docs: sourceSocials.docs || "",
  };
  const brandingForm = useForm({
    defaultValues: {
      branding: {
        smtp: defaultSmtp,
        socials: defaultSocial,
      },
    },
  });
  useEffect(() => {
    brandingForm.setValue("branding", {
      ...agency?.agencyData?.branding,
      smtp: { ...defaultSmtp },
      socials: { ...defaultSocial },
    });

    if (agencyData?.id && agencyData?.branding?.logo_url) {
      const baseUrl =
        window.location.origin.includes("insighto.ai") ||
        window.location.origin.includes("localhost")
          ? "https://uc.insighto.ai"
          : window.location.origin;
      const agencyLogoUrl = agencyData.branding.logo_url.includes(window.origin)
        ? agencyData.branding.logo_url
        : `${baseUrl}${agencyData.branding.logo_url}`;
      setLogo(agencyLogoUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agency?.agencyData]);

  if (isLoading) return <Loader />;

  const sections = [
    {
      id: "identity",
      label: "Identity",
      panel: (
        <IdentityPanel
          agency={agency}
          isValidate={isValidate}
          canWrite={canWrite}
        />
      ),
    },
    {
      id: "logo-color",
      label: "Logo & color",
      panel: (
        <LogoColorPanel
          agency={agency}
          logo={logo}
          handleLogoChange={handleLogoChange}
          handleLogoRemove={handleLogoRemove}
          canWrite={canWrite}
        />
      ),
    },
    {
      id: "email",
      label: "Email (SMTP)",
      panel: (
        <SectionShell
          title="Email server (SMTP)"
          description="Outbound mail from the subaccount portal — invites, password resets, billing receipts — goes through this server."
        >
          <Smtp brandingForm={brandingForm} />
        </SectionShell>
      ),
    },
    {
      id: "social",
      label: "Social",
      panel: (
        <SectionShell
          title="Social presence"
          description="Your public social profiles. Surfaced in the subaccount portal footer."
        >
          <Social brandingForm={brandingForm} getAgencies={getAgencies} />
        </SectionShell>
      ),
    },
    {
      id: "support",
      label: "Support",
      panel: (
        <SectionShell
          title="Support resources"
          description="Where your subaccount users go for help."
        >
          <SupportLinks
            brandingForm={brandingForm}
            getAgencies={getAgencies}
          />
        </SectionShell>
      ),
    },
    {
      id: "script",
      label: "Tracking script",
      panel: (
        <SectionShell
          title="Tracking script"
          description="Inject a GA / GTM / Pixel snippet into every subaccount portal page."
        >
          <AgencyScript
            brandingForm={brandingForm}
            getAgencies={getAgencies}
          />
        </SectionShell>
      ),
    },
  ];

  return (
    <Flex direction="column" gap={4} w="100%" pb={6}>
      {/* Page header */}
      <Box>
        <Heading color={textColor} fontSize="xl">
          Branding
        </Heading>
        <Text fontSize="sm" color="gray.500" mt={1}>
          Customize how your portal appears to subaccounts.
        </Text>
      </Box>

      {!agencyData && (
        <Alert status="info" borderRadius="md" variant="left-accent">
          <AlertIcon />
          <AlertDescription fontSize="sm">
            Set your agency name on the <strong>Identity</strong> tab
            first — most other settings unlock once the agency is
            created.
          </AlertDescription>
        </Alert>
      )}

      {/* Vertical-left tabs — settings-page pattern */}
      <Tabs
        orientation="vertical"
        variant="line"
        colorScheme={buttonColorScheme}
        isLazy
        align="start"
      >
        <TabList
          minW={{ base: "140px", md: "200px" }}
          borderRightWidth="1px"
          mr={6}
        >
          {sections.map((s) => (
            <Tab
              key={s.id}
              justifyContent="flex-start"
              py={3}
              fontSize="sm"
              isDisabled={
                !agencyData && s.id !== "identity"
              }
            >
              {s.label}
            </Tab>
          ))}
        </TabList>
        <TabPanels>
          {sections.map((s) => (
            <TabPanel key={s.id} pt={0} px={0}>
              {s.panel}
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
    </Flex>
  );
};

export default Branding;
